import { useQuery } from '@tanstack/react-query';
import { PageHeader, PageHeaderSkeleton, Stack, useDelayedLoading } from '@leanin/ui';
import {
  fetchCircleJoinRequests,
  fetchNetworkJoinRequests,
  groupKeys,
} from '../../../lib/queries/groups';
import {
  useApproveJoinRequest,
  useDeclineJoinRequest,
} from '../../../lib/mutations/groups';
import { useMessageMember } from '../../groups/use-message-member';
import { InlineStatus } from '../../InlineStatus';
import { AdminJoinRequestsCard } from './AdminJoinRequestsCard';
import { AdminJoinRequestsCardSkeleton } from './AdminJoinRequestsCard.skeleton';

// Pending join requests, for circles and networks. (Spaces don't have
// request-to-join.) Each kind's queries differ in URL but share a shape;
// dispatched on `kind`.

type Props = {
  readonly kind: 'circle' | 'network';
  readonly groupId: string;
};

export function GroupRequestsSection({ kind, groupId }: Props) {
  // Parallel queries, each gated by `kind` via `enabled`. The inactive one
  // is a no-op; the active one populates `query`. This shape keeps TS
  // overload inference clean (no conditional config arg into useQuery).
  const circleQuery = useQuery({
    queryKey: groupKeys.circle.joinRequests(groupId),
    queryFn: () => fetchCircleJoinRequests(groupId),
    enabled: kind === 'circle',
  });
  const networkQuery = useQuery({
    queryKey: groupKeys.network.joinRequests(groupId),
    queryFn: () => fetchNetworkJoinRequests(groupId),
    enabled: kind === 'network',
  });
  const query = kind === 'circle' ? circleQuery : networkQuery;

  const approve = useApproveJoinRequest();
  const decline = useDeclineJoinRequest();
  const { messageMember, error: messageError } = useMessageMember();
  const isPending = approve.isPending || decline.isPending;

  const handleApprove = (requestId: string) => {
    approve.mutate({ groupId, requestId, kind });
  };
  const handleDecline = (requestId: string) => {
    decline.mutate({ groupId, requestId, kind });
  };

  const showSkeleton = useDelayedLoading(query.data === undefined);
  if (showSkeleton) {
    return (
      <Stack gap="lg">
        <PageHeaderSkeleton />
        <AdminJoinRequestsCardSkeleton rows={3} />
      </Stack>
    );
  }
  if (query.data === undefined) return null;

  const requests = query.data.items.filter((r) => r.status === 'pending');

  return (
    <Stack gap="lg">
      <PageHeader
        title="Pending requests"
        subtitle="Approve or decline new member requests below."
      />
      {messageError !== null ? (
        <InlineStatus>{messageError}</InlineStatus>
      ) : null}
      <AdminJoinRequestsCard
        requests={requests}
        onApprove={handleApprove}
        onDecline={handleDecline}
        onMessage={messageMember}
        isPending={isPending}
      />
    </Stack>
  );
}
